/**
 * Помощники редактора текста страницы: что считать пустым текстом, какие
 * адреса принимать и какие цвета предлагать. Отдельно от компонента — это
 * обычные функции, их проверяют без редактора.
 */

/**
 * HTML, который уходит на сервер. Tiptap пустой текст отдаёт как `<p></p>`,
 * а в конце документа сам держит пустой абзац, чтобы под таблицей или
 * списком было куда поставить курсор. Ни то ни другое — не текст: пустой
 * редактор сохраняется пустой строкой, и язык остаётся «Не заполнено».
 */
export function cleanHtml(html: string): string {
  return html.replace(/(<p><\/p>)+$/, '')
}

/**
 * Адрес ссылки. Без схемы — считаем сайтом: «example.uz» значит
 * «https://example.uz», а не относительный путь на витрине.
 */
export function linkHref(raw: string): string {
  const url = raw.trim()
  if (!url || /^(https?:|mailto:|tel:|\/)/i.test(url)) return url
  return `https://${url}`
}

/**
 * Адрес картинки — только https: страница открывается по https, и картинка
 * по http была бы заблокирована браузером. Загрузки файлов в API нет,
 * поэтому картинка вставляется только по адресу. Не подходит — `null`.
 */
export function imageSrc(raw: string): string | null {
  const url = raw.trim()
  return /^https:\/\/\S+$/i.test(url) ? url : null
}

/**
 * Вставка из Word и Google Docs: структуру (заголовки, списки, таблицы,
 * жирный) разбирает схема редактора, а неизвестные ей стили отбрасывает сама.
 * Цвет текста и фона схема знает, поэтому его снимаем здесь: Google Docs
 * красит каждый кусок в чёрный, и в тёмной теме приложения такой текст не
 * прочитать. Копирование внутри редактора не трогаем — там цвет выбран
 * в панели.
 */
export function dropPastedColors(html: string): string {
  if (html.includes('data-pm-slice')) return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
    el.style.removeProperty('color')
    el.style.removeProperty('background-color')
    el.style.removeProperty('background')
  })
  return doc.body.innerHTML
}

/** Цвет в виде `rgb(r,g,b)`: так одинаково читаются «#b9413b» и «rgb(185, 65, 59)». */
function toRgb(color: string): string {
  const hex = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color.trim())
  if (!hex) return color.replace(/\s+/g, '').toLowerCase()
  return `rgb(${hex
    .slice(1)
    .map((part) => parseInt(part, 16))
    .join(',')})`
}

/**
 * Тот же ли цвет. Выбранный в панели цвет редактор помнит как есть, а
 * сохранённый текст браузер возвращает в `rgb()` — сравниваем по значению.
 */
export function sameColor(current: string | undefined, swatch: string): boolean {
  return current !== undefined && toRgb(current) === toRgb(swatch)
}

export interface Swatch {
  name: string
  value: string
}

/*
 * Цвета — значения общей палитры (shared/ui/src/styles/system.css,
 * admin/src/styles/system.css). В сохранённый HTML уходит сам цвет, а не
 * переменная: текст показывает и приложение покупателя, и переменных
 * админки там нет. Тёмные — для текста (контраст на белом не ниже 4,5:1),
 * светлые — для выделения фоном.
 */
export const TEXT_COLORS: Swatch[] = [
  { name: 'Зелёный', value: '#00523f' },
  { name: 'Красный', value: '#b9413b' },
  { name: 'Янтарный', value: '#a85a00' },
  { name: 'Синий', value: '#2f5d78' },
  { name: 'Фиолетовый', value: '#54479a' },
  { name: 'Серый', value: '#5f6e66' },
]

export const HIGHLIGHTS: Swatch[] = [
  { name: 'Жёлтый', value: '#fcf3cc' },
  { name: 'Зелёный', value: '#dfeee8' },
  { name: 'Красный', value: '#fbe7e5' },
  { name: 'Голубой', value: '#e3eef6' },
  { name: 'Сиреневый', value: '#eceaf5' },
]
